import {
  BellOutlined,
  CalendarOutlined,
  CreditCardOutlined,
  SearchOutlined,
  SmileOutlined,
  TeamOutlined,
  ThunderboltOutlined,
} from "@ant-design/icons";
import { Typography } from "antd";
import { useTranslation } from "react-i18next";
import styled, { keyframes } from "styled-components";

const { Text } = Typography;

const steps = [
  { icon: <SearchOutlined />, titleKey: "StepChooseTitle", descKey: "StepChooseDesc" },
  { icon: <TeamOutlined />, titleKey: "StepPickTitle", descKey: "StepPickDesc" },
  { icon: <CalendarOutlined />, titleKey: "StepBookTitle", descKey: "StepBookDesc" },
];

const benefits = [
  { icon: <CreditCardOutlined />, titleKey: "BenefitFreeTitle", descKey: "BenefitFreeDesc" },
  { icon: <BellOutlined />, titleKey: "BenefitRemindersTitle", descKey: "BenefitRemindersDesc" },
  { icon: <ThunderboltOutlined />, titleKey: "BenefitFastTitle", descKey: "BenefitFastDesc" },
  { icon: <SmileOutlined />, titleKey: "BenefitEasyTitle", descKey: "BenefitEasyDesc" },
];

interface Props {
  showDescriptions?: boolean;
}

export const StepsAndBenefits: React.FC<Props> = ({ showDescriptions = false }) => {
  const { t } = useTranslation();

  return (
    <>
      {/* Steps */}
      <StepsHeader>
        <StepsTitle>{t("HowItWorksTitle")}</StepsTitle>
        <StepsSubtitle>{t("HowItWorksSubtitle")}</StepsSubtitle>
      </StepsHeader>
      <StepsSection>
        {steps.map((step, i) => (
          <StepCard key={step.titleKey} $compact={!showDescriptions}>
            <StepNumber>{i + 1}</StepNumber>
            <StepIcon>{step.icon}</StepIcon>
            <StepTitle>{t(step.titleKey)}</StepTitle>
            {showDescriptions && <StepDesc>{t(step.descKey)}</StepDesc>}
          </StepCard>
        ))}
      </StepsSection>

      {/* Benefits */}
      <BenefitsHeader>{t("WhyRasporediMe")}</BenefitsHeader>
      <BenefitsGrid>
        {benefits.map((b) => (
          <BenefitCard key={b.titleKey}>
            <BenefitIcon>{b.icon}</BenefitIcon>
            <BenefitContent>
              <BenefitTitle>{t(b.titleKey)}</BenefitTitle>
              <BenefitDesc>{t(b.descKey)}</BenefitDesc>
            </BenefitContent>
          </BenefitCard>
        ))}
      </BenefitsGrid>
    </>
  );
};

// ─── Styles ─────────────────────────────────────────────────────────────────

const fadeInUp = keyframes`
  from { opacity: 0; transform: translateY(16px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const StepsHeader = styled.div`
  text-align: center;
  margin-bottom: 24px;
`;

const StepsTitle = styled.h3`
  font-size: 1.3em;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 6px;
`;

const StepsSubtitle = styled(Text)`
  &.ant-typography {
    font-size: 14px;
    color: #64748b;
    line-height: 1.6;
  }
`;

const StepsSection = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 16px;
`;

const StepCard = styled.div<{ $compact?: boolean }>`
  position: relative;
  background: #fff;
  border-radius: 20px;
  padding: ${(p) => (p.$compact ? "24px 16px 16px" : "28px 20px 20px")};
  text-align: center;
  border: 1.5px solid #FDF0F4;
  box-shadow: 0 2px 12px rgba(216, 27, 96, 0.08);
  opacity: 0;
  animation: ${fadeInUp} 0.4s ease-out forwards;
`;

const StepNumber = styled.div`
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: linear-gradient(135deg, #D81B60, #FF7F6B);
  color: #fff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const StepIcon = styled.div`
  font-size: 28px;
  color: #D81B60;
  margin-bottom: 8px;
`;

const StepTitle = styled(Text)`
  &.ant-typography {
    display: block;
    font-weight: 700;
    font-size: 15px;
    color: #1e293b;
    margin-bottom: 4px;
  }
`;

const StepDesc = styled(Text)`
  &.ant-typography {
    font-size: 13px;
    color: #64748b;
    line-height: 1.5;
  }
`;

const BenefitsHeader = styled.h3`
  text-align: center;
  font-size: 1.3em;
  font-weight: 700;
  color: #1e293b;
  margin: 32px 0 20px;
`;

const BenefitsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const BenefitCard = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 14px;
  background: #fff;
  border-radius: 16px;
  padding: 18px;
  border: 1.5px solid #FDF0F4;
  box-shadow: 0 2px 8px rgba(216, 27, 96, 0.06);
  height: 100%;
  transition: transform 0.25s ease, box-shadow 0.25s ease;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 16px rgba(216, 27, 96, 0.12);
  }
`;

const BenefitIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 12px;
  background: #FDF0F4;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  color: #D81B60;
  flex-shrink: 0;
`;

const BenefitContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  /* Zauzima ostatak kartice pored ikone; bez toga je blok širok koliko opis,
     pa je naslov bio centriran samo iznad njega. */
  flex: 1;
  min-width: 0;
  text-align: center;
`;

const BenefitTitle = styled(Text)`
  &.ant-typography {
    font-weight: 700;
    font-size: 14px;
    color: #1e293b;
  }
`;

const BenefitDesc = styled(Text)`
  &.ant-typography {
    font-size: 13px;
    color: #64748b;
    line-height: 1.5;
  }
`;
